import { type FormEvent, useEffect, useState } from "react";

import {
  APIError,
  cancelDeviceOperations,
  deviceLabel,
  getDeviceHeartbeat,
  getDeviceOperations,
  getDeviceNetworkUsage,
  resetDeviceNetworkUsage,
  setDeviceHeartbeat,
  setDeviceNetworkUsage,
  type Device,
  type DeviceHeartbeat,
  type DeviceNetworkUsage,
  type DeviceOperations,
  type NetworkUsageConfig,
} from "./api";
import { DeviceNotificationSettings } from "./Notifications";
import { DeviceStorageSettings } from "./Storage";
import { MeasurementDeliverySettings } from "./DeliverySettings";
import { displayLocale } from "./localization";
import { decimalGB, formatNetworkBytes } from "./networkUsageFormat";

function initialSettings(usage?: DeviceNetworkUsage): NetworkUsageConfig {
  return usage?.settings ?? {
    schema: 1,
    enabled: false,
    period: "never",
    timezone: "Europe/Berlin",
    reset_generation: 0,
    interfaces: [],
  };
}

export function AgentSettings({
  device,
  onChanged,
  onError,
  onNotice,
}: {
  device: Device;
  onChanged: (usage: DeviceNetworkUsage) => void;
  onError: (message: string) => void;
  onNotice: (message: string) => void;
}) {
  const [usage, setUsage] = useState<DeviceNetworkUsage | undefined>(device.network_usage);
  const [settings, setSettings] = useState<NetworkUsageConfig>(initialSettings(device.network_usage));
  const [quotaGB, setQuotaGB] = useState(device.network_usage?.settings.quota_bytes ? String(device.network_usage.settings.quota_bytes / decimalGB) : "");
  const [interfaces, setInterfaces] = useState((device.network_usage?.settings.interfaces ?? []).join(", "));
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [heartbeat, setHeartbeat] = useState<DeviceHeartbeat | undefined>(device.heartbeat);
  const [heartbeatInterval, setHeartbeatInterval] = useState(device.heartbeat?.interval_s ?? 60);
  const [offlineAfter, setOfflineAfter] = useState(device.heartbeat?.offline_after_s ?? (device.heartbeat?.interval_s ?? 60) * 2);
	const [operations, setOperations] = useState<DeviceOperations>();

  useEffect(() => {
    let active = true;
    Promise.all([getDeviceNetworkUsage(device.id), getDeviceHeartbeat(device.id), getDeviceOperations(device.id)])
      .then(([value, heartbeatValue, operationsValue]) => {
        if (!active) return;
        setUsage(value);
        setSettings(value.settings);
        setQuotaGB(value.settings.quota_bytes ? String(value.settings.quota_bytes / decimalGB) : "");
        setInterfaces((value.settings.interfaces ?? []).join(", "));
        setHeartbeat(heartbeatValue);
        setHeartbeatInterval(heartbeatValue.interval_s);
        setOfflineAfter(heartbeatValue.offline_after_s);
		setOperations(operationsValue);
      })
      .catch((reason: unknown) => {
        if (active) onError(reason instanceof APIError && reason.status === 401 ? "Die Sitzung ist abgelaufen." : "Die Agent-Einstellungen konnten nicht geladen werden.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [device.id, onError]);

  async function saveHeartbeat(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!Number.isInteger(heartbeatInterval) || heartbeatInterval < 10 || heartbeatInterval > 3600) {
      onError("Das Heartbeat-Intervall muss zwischen 10 und 3600 Sekunden liegen.");
      return;
    }
    if (!Number.isInteger(offlineAfter) || offlineAfter < heartbeatInterval || offlineAfter > 86400) {
      onError("Die Offline-Zeit muss mindestens dem Heartbeat-Intervall entsprechen und darf höchstens 86400 Sekunden betragen.");
      return;
    }
    setBusy(true);
    onError("");
    try {
      const changed = await setDeviceHeartbeat(device.id, heartbeatInterval, offlineAfter);
      setHeartbeat(changed);
      setHeartbeatInterval(changed.interval_s);
      setOfflineAfter(changed.offline_after_s);
      onNotice(`Heartbeat für ${deviceLabel(device)}: alle ${changed.interval_s} Sekunden, offline nach ${changed.offline_after_s} Sekunden.`);
    } catch (reason: unknown) {
      if (reason instanceof APIError && reason.status === 409) {
        onError("Für diese Einstellung muss der Agent zuerst aktualisiert werden.");
      } else {
        onError("Das Heartbeat-Intervall konnte nicht gespeichert oder an das Gerät übertragen werden.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsedQuota = quotaGB.trim() === "" ? 0 : Number(quotaGB.replace(",", "."));
    if (!Number.isFinite(parsedQuota) || parsedQuota <= 0 && quotaGB.trim() !== "") {
      onError("Bitte ein gültiges Kontingent in GB eingeben.");
      return;
    }
    const parsedInterfaces = interfaces.split(",").map((value) => value.trim()).filter(Boolean);
    const next: NetworkUsageConfig = {
      ...settings,
      schema: 1,
      reset_day: settings.period === "calendar_month" ? settings.reset_day || 1 : undefined,
      quota_bytes: parsedQuota > 0 ? Math.round(parsedQuota * decimalGB) : undefined,
      warning_percent: parsedQuota > 0 ? settings.warning_percent || 80 : undefined,
      reset_generation: 0,
      interfaces: parsedInterfaces,
    };
    setBusy(true);
    onError("");
    try {
      const changed = await setDeviceNetworkUsage(device.id, next);
      setUsage(changed);
      setSettings(changed.settings);
      onChanged(changed);
      onNotice(`Agent-Einstellungen für ${deviceLabel(device)} wurden gespeichert.`);
    } catch {
      onError("Die Agent-Einstellungen konnten nicht gespeichert oder an das Gerät übertragen werden.");
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    if (!window.confirm(`Netzwerkzähler für ${deviceLabel(device)} jetzt auf null setzen? Der bisherige Zeitraum wird verworfen.`)) return;
    setBusy(true);
    onError("");
    try {
      const changed = await resetDeviceNetworkUsage(device.id);
      setUsage(changed);
      setSettings(changed.settings);
      onChanged(changed);
      onNotice(`Der Netzwerkzähler für ${deviceLabel(device)} wird zurückgesetzt.`);
    } catch {
      onError("Der Netzwerkzähler konnte nicht zurückgesetzt werden.");
    } finally {
      setBusy(false);
    }
  }

  async function cancelQueues() {
	if (!window.confirm(`Alle laufenden und wartenden Aufträge für ${deviceLabel(device)} stoppen? Development-/Release-/Löschaufträge sowie ein vorgemerktes Agent-Update werden abgebrochen; installierte Releases und Messdaten bleiben erhalten.`)) return;
	setBusy(true); onError("");
	try {
	  const result = await cancelDeviceOperations(device.id);
	  setOperations(await getDeviceOperations(device.id));
	  onNotice(`Warteschlangen für ${deviceLabel(device)} wurden gestoppt (Audit ${result.generation}).`);
	} catch (reason: unknown) {
	  if (reason instanceof APIError && reason.status === 409) onError("Auf diesem Pi läuft gerade ein nicht sicher unterbrechbarer Releasewechsel.");
	  else onError("Die Warteschlangen konnten nicht vollständig gestoppt werden.");
	} finally { setBusy(false); }
  }

  if (loading) return <p className="agent-settings-loading">Agent-Einstellungen werden geladen …</p>;

  return <div className="agent-settings-stack">
    <DeviceNotificationSettings key={`notifications-${device.id}`} deviceID={device.id} />
    <MeasurementDeliverySettings key={`delivery-${device.id}`} deviceID={device.id} />
    <DeviceStorageSettings key={device.id} device={device} onError={onError} onNotice={onNotice} />
	{operations?.config_rejection && <section className="agent-config-rejection" role="alert">
	  <strong>Konfiguration vom Pi abgelehnt</strong>
	  <p>Zielversion {operations.config_rejection.requested_version}, weiterhin aktiv {operations.config_rejection.applied_version} · Agent {operations.agent_version || "unbekannt"}</p>
	  <dl><div><dt>Fehlercode</dt><dd>{operations.config_rejection.error_code}</dd></div>{operations.config_rejection.diagnostic && <div><dt>Technische Ursache</dt><dd>{operations.config_rejection.diagnostic}</dd></div>}<div><dt>Zeitpunkt</dt><dd>{new Date(operations.config_rejection.rejected_at).toLocaleString(displayLocale)}</dd></div></dl>
	</section>}
    <form className="agent-settings-form" onSubmit={saveHeartbeat}>
      <div className="agent-settings-heading">
        <div><strong>Heartbeat</strong><span>Legt fest, wie oft der Pi seinen Online-Status sendet.</span></div>
        <small>Offline nach {offlineAfter} Sekunden ohne Heartbeat</small>
      </div>
      <div className="agent-settings-grid">
        <label>Intervall in Sekunden<input type="number" min={10} max={3600} step={1} value={heartbeatInterval} disabled={!heartbeat?.supported} onChange={(event) => setHeartbeatInterval(Number(event.target.value))} /></label>
        <label>Offline nach (Sekunden)<input type="number" min={heartbeatInterval} max={86400} step={1} required value={offlineAfter} disabled={!heartbeat?.supported} onChange={(event) => setOfflineAfter(Number(event.target.value))} /></label>
      </div>
      {heartbeat && !heartbeat.supported && <p className="agent-settings-current attention">Agent-Update auf Version 0.27.0 oder neuer erforderlich.</p>}
      <p className="agent-settings-current">Standard: offline nach dem doppelten Heartbeat-Intervall. Die Offline-Zeit gilt für die Anzeige und Gerätewarnungen; sie erzeugt keinen zusätzlichen Datenverkehr.</p>
      <div className="agent-settings-actions"><button className="primary-button compact-primary" disabled={busy || !heartbeat?.supported}>Heartbeat speichern</button></div>
    </form>
    <form className="agent-settings-form" onSubmit={save}>
    <div className="agent-settings-heading">
      <div><strong>Netzwerkverbrauch</strong><span>Ein- und ausgehender Verkehr der Internet-Schnittstelle(n).</span></div>
      {usage?.status && <small>{usage.synchronized ? `Stand ${new Date(usage.status.sampled_at).toLocaleString(displayLocale)}` : "Änderung noch nicht vom Agent bestätigt"}</small>}
    </div>
    <label className="agent-settings-toggle"><input type="checkbox" checked={settings.enabled} onChange={(event) => setSettings({ ...settings, enabled: event.target.checked })} />Netzwerkverkehr zählen</label>
    <div className="agent-settings-grid">
      <label>Zeitraum<select value={settings.period} onChange={(event) => setSettings({ ...settings, period: event.target.value as NetworkUsageConfig["period"] })}>
        <option value="never">Fortlaufend, nur manuell zurücksetzen</option>
        <option value="calendar_month">Monatstag bis Monatstag</option>
        <option value="fixed_30_days">Jeweils 30 Tage</option>
      </select></label>
      {settings.period === "calendar_month" && <label>Reset am Monatstag<input type="number" min={1} max={31} value={settings.reset_day || 1} onChange={(event) => setSettings({ ...settings, reset_day: Number(event.target.value) })} /></label>}
      <label>Kontingent in GB<input type="text" inputMode="decimal" placeholder="z. B. 5" value={quotaGB} onChange={(event) => setQuotaGB(event.target.value)} /></label>
      <label>Warnung ab Prozent<input type="number" min={1} max={100} disabled={!quotaGB.trim()} value={settings.warning_percent || 80} onChange={(event) => setSettings({ ...settings, warning_percent: Number(event.target.value) })} /></label>
      <label>Zeitzone<input required maxLength={64} value={settings.timezone} onChange={(event) => setSettings({ ...settings, timezone: event.target.value })} /></label>
    </div>
    <details className="technical-disclosure"><summary>Erweiterte Schnittstellenauswahl</summary>
      <label>Schnittstellen, durch Komma getrennt<input value={interfaces} placeholder="Automatisch anhand der Standardroute" onChange={(event) => setInterfaces(event.target.value)} /></label>
      <p>Leer zählt die aktiven Internet-Uplinks automatisch. Virtuelle oder lokale Schnittstellen werden so nicht doppelt gezählt.</p>
    </details>
    {usage?.status?.state === "active" && <p className={`agent-settings-current ${usage.warning ? "attention" : ""}`}>
      Aktuell {formatNetworkBytes(usage.total_bytes)} · In {formatNetworkBytes(usage.status.rx_bytes)} · Out {formatNetworkBytes(usage.status.tx_bytes)}
    </p>}
    {usage?.status?.state === "active" && usage.status.scope === "interface" && <p className="agent-settings-current attention">Dieser Wert enthält auch lokalen Netzwerkverkehr, weil die WAN-Zählung auf dem Gerät nicht verfügbar ist. Für ein Tarifkontingent ist er dadurch deutlich zu hoch.</p>}
    {usage?.status?.state === "error" && <p className="agent-settings-current attention">Der Agent kann den Netzwerkverbrauch derzeit nicht erfassen ({usage.status.error_code || "unbekannter Fehler"}).</p>}
    <div className="agent-settings-actions">
      <button className="primary-button compact-primary" disabled={busy}>Speichern</button>
      <button className="text-action" type="button" disabled={busy || !settings.enabled} onClick={() => void reset()}>Zähler jetzt zurücksetzen</button>
    </div>
    </form>
	<section className="agent-settings-form agent-queue-control">
	  <div className="agent-settings-heading"><div><strong>Warteschlangen und laufende Aufträge</strong><span>Bricht Development-, Release-, Lösch- und vorgemerkte Agent-Aufträge ab und bereinigt deren lokalen Arbeitsstand.</span></div><small>{(operations?.pending_attempts || 0) + (operations?.pending_deletions || 0) + (operations?.pending_agent_update ? 1 : 0)} offen</small></div>
	  {!operations?.cancel_supported && <p className="agent-settings-current attention">Diese Funktion benötigt Agent 0.27.5 oder neuer.</p>}
	  <div className="agent-settings-actions"><button className="danger-button" type="button" disabled={busy || !operations?.cancel_supported} onClick={() => void cancelQueues()}>Alle Warteschlangenaufträge stoppen</button></div>
	</section>
  </div>;
}
